import { type ChangeEvent, useState } from "react";

import { SecondaryButton } from "@vega-core/components/Buttons/SecondaryButton";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { TextareaInput } from "@vega-core/components/Form/Controls/Textarea/TextareaInput";
import { m } from "@vega-core/paraglide/messages";
import { cnTwMerge } from "@vega-core/utils/cnTwMerge";

const RATING_SCALE = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10] as const;
const DATA_TEST_ID = "user-feedback-content";

export type UserFeedbackContentProps = {
  onSubmit: (payload: { rating: number | null; suggestions: string }) => void;
  onSkip: () => void;
  isSubmitting?: boolean;
  title?: string;
  question?: string;
  scaleQuestion?: string;
  scaleMinLabel?: string | null;
  scaleMaxLabel?: string | null;
  improvementLabel?: string;
  improvementPlaceholder?: string | null;
};

export const UserFeedbackContent = ({
  onSubmit,
  onSkip,
  isSubmitting = false,
  title,
  question,
  scaleQuestion,
  scaleMinLabel,
  scaleMaxLabel,
  improvementLabel,
  improvementPlaceholder,
}: UserFeedbackContentProps) => {
  const [rating, setRating] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState("");

  const hasRatingQuestion = Boolean(scaleQuestion);
  const hasFreeTextQuestion = Boolean(improvementLabel);

  if (!hasRatingQuestion && !hasFreeTextQuestion) return null;

  const isSubmitDisabled =
    isSubmitting || (hasRatingQuestion && rating === null);

  const handleSubmit = () => {
    if (hasRatingQuestion && rating === null) return;
    onSubmit({ rating, suggestions });
  };

  const handleSuggestionsChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setSuggestions(event.target.value);
  };

  return (
    <div
      className="flex flex-col gap-6 w-full max-w-xl p-6 md:p-8 bg-background/primary text-text/primary rounded-lg"
      data-testid={DATA_TEST_ID}
    >
      {title || question ? (
        <div className="flex flex-col gap-2 text-center">
          {title ? (
            <h2 className="heading-4 text-text/primary">{title}</h2>
          ) : null}
          {question ? (
            <p className="body-base-regular text-text/secondary">{question}</p>
          ) : null}
        </div>
      ) : null}

      {hasRatingQuestion ? (
        <div className="flex flex-col gap-4">
          <p className="body-base-semibold text-text/primary">
            {scaleQuestion}
          </p>
          <div
            className="flex justify-between items-center self-stretch gap-2"
            data-testid={`${DATA_TEST_ID}-rating-buttons`}
          >
            {RATING_SCALE.map((num) => (
              <ThemedButton
                className={cnTwMerge(
                  "w-10 h-10 rounded-lg border border-border/default p-1.5 body-base-semibold text-text/primary hover:bg-background/tertiary/40",
                  rating === num && "bg-brand-default/10 border-brand-default",
                )}
                dataTestId={`${DATA_TEST_ID}-rating-${num}`}
                disabled={isSubmitting}
                key={num}
                onClick={() => setRating(num)}
                variant="tertiary"
              >
                {num}
              </ThemedButton>
            ))}
          </div>
          <div className="flex gap-2">
            <span
              className="flex-1 body-small-regular text-text/secondary text-left"
              data-testid={`${DATA_TEST_ID}-rating-label-min`}
            >
              {scaleMinLabel}
            </span>
            <span
              className="flex-1 body-small-regular text-text/secondary text-right"
              data-testid={`${DATA_TEST_ID}-rating-label-max`}
            >
              {scaleMaxLabel}
            </span>
          </div>
        </div>
      ) : null}

      {hasFreeTextQuestion ? (
        <div className="flex flex-col gap-2">
          <p className="body-base-semibold text-text/primary">
            {improvementLabel}
          </p>
          <TextareaInput
            dataTestId={`${DATA_TEST_ID}-suggestions-input`}
            disabled={isSubmitting}
            fullWidth
            name="suggestions"
            onChange={handleSuggestionsChange}
            placeholder={improvementPlaceholder ?? undefined}
            rows={4}
            value={suggestions}
            withBorder
          />
          <p className="body-small-regular text-text/secondary text-right">
            {m.feedback_optional()}
          </p>
        </div>
      ) : null}

      <div className="flex flex-col gap-3">
        <ThemedButton
          dataTestId={`${DATA_TEST_ID}-submit-button`}
          disabled={isSubmitDisabled}
          fullWidth
          isLoading={isSubmitting}
          onClick={handleSubmit}
          variant="primary"
        >
          {m.feedback_submitFeedback()}
        </ThemedButton>
        <SecondaryButton
          dataTestId={`${DATA_TEST_ID}-skip-button`}
          disabled={isSubmitting}
          onClick={onSkip}
        >
          {m.feedback_notNow()}
        </SecondaryButton>
      </div>
    </div>
  );
};
